import { isWeb, getSecureValue, ENVIRONMENTS_NAME } from '@helpers';
import { getTrackingInstance, isTrackingInitialized } from './initTracking';
import type { UserInfo, UserPropertyUpdate } from '@services/tracking/types';
import '@services/tracking/types';
import { INTERNAL_DOMAINS } from '@services/tracking/config';

const isInternalUser = (email: UserInfo['emailAddress']): boolean => {
  if (!email) return false;
  return INTERNAL_DOMAINS.some((d) => email.toLowerCase().endsWith(d));
};

export const identifyUser = (userInfo: UserInfo): void => {
  if (!isTrackingInitialized()) return;

  const tenantId = getSecureValue('tid') || '';
  const envKey = getSecureValue('env') as keyof typeof ENVIRONMENTS_NAME;
  const env = ENVIRONMENTS_NAME[envKey] || 'Unknown';
  const instance = getTrackingInstance();

  if (!instance) return;

  instance.setOptOut(isInternalUser(userInfo.emailAddress));
  instance.setUserId(userInfo.emailAddress);

  const name = `${userInfo.userProfile?.firstName || ''} ${userInfo.userProfile?.lastName || ''}`.trim();
  const email = userInfo.emailAddress || '';
  const role = userInfo.role || '';
  const company = userInfo.companyProfile?.companyName || '';
  const language = userInfo.userProfile?.preferedLanguage || '';

  if (isWeb && window.amplitude?.Identify) {
    // web: use browser snippet's Identify class
    const identify = new window.amplitude.Identify()
      .set('Name', name)
      .set('Email', email)
      .set('Role', role)
      .set('Company', company)
      .set('Tenant ID', tenantId)
      .set('Language', language)
      .set('App Environment', env)
      .set('Backend Environment', env);

    window.amplitude.identify(identify);
  } else if (instance.Identify) {
    // for native devices we use SDK's Identify class
    const identify = new instance.Identify()
      .set('Name', name)
      .set('Email', email)
      .set('Role', role)
      .set('Company', company)
      .set('Tenant ID', tenantId)
      .set('Language', language)
      .set('App Environment', env)
      .set('Backend Environment', env);

    instance.identify(identify);
  }
};

// Deferred user enrichment - machine/ERP data is not yet available during identifyUser() at login
export const updateUserProperties = (properties: UserPropertyUpdate): void => {
  if (!isTrackingInitialized()) return;

  const instance = getTrackingInstance();
  if (!instance) return;

  if (isWeb && window.amplitude?.Identify) {
    const identify = new window.amplitude.Identify()
      .set('Machine Count', properties.machineCount)
      .set('Demo Machine Count', properties.demoMachineCount)
      .set('Has ERP Integration', properties.hasERPIntegration);

    window.amplitude.identify(identify);
  } else if (instance.Identify) {
    const identify = new instance.Identify()
      .set('Machine Count', properties.machineCount)
      .set('Demo Machine Count', properties.demoMachineCount)
      .set('Has ERP Integration', properties.hasERPIntegration);

    instance.identify(identify);
  }
};
